import React from "react";
import { Link } from "react-router-dom";
import { BUSINESS, SERVICES, CITIES } from "@/data/config";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-ink-950 border-t border-white/10 mt-24">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        {/* Big CTA row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-16 border-b border-white/10">
          <div className="lg:col-span-8">
            <p className="overline mb-4">Ready when you are</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest text-bone-50 max-w-3xl">
              Get a real, on site quote by Friday.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col justify-end gap-4">
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="group inline-flex items-center justify-between gap-4 bg-copper-500 hover:bg-copper-600 text-ink-950 hover:text-white font-heading font-semibold text-lg px-6 py-5 rounded-sm transition-colors duration-200"
            >
              Request a Free Quote
              <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="footer-phone-cta"
              className="inline-flex items-center justify-between gap-4 border border-white/15 hover:border-copper-500 text-bone-50 font-heading font-semibold text-lg px-6 py-5 rounded-sm transition-colors duration-200"
            >
              Call {BUSINESS.phone}
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-16 border-b border-white/10">
          <div>
            <div className="overline mb-5">Services</div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="text-bone-200 hover:text-copper-500 text-sm md:text-base"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-5">Service areas</div>
            <ul className="space-y-3">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${c.slug}`}
                    className="text-bone-200 hover:text-copper-500 text-sm md:text-base"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-5">Company</div>
            <ul className="space-y-3 text-bone-200 text-sm md:text-base">
              <li><Link to="/about" className="hover:text-copper-500">About</Link></li>
              <li><Link to="/contact" className="hover:text-copper-500">Contact</Link></li>
              <li><Link to="/sitemap" className="hover:text-copper-500" data-testid="footer-sitemap-link">HTML Sitemap</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-5">Reach us</div>
            <ul className="space-y-3 text-bone-200 text-sm md:text-base">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-1 text-copper-500 shrink-0" />
                <a href={BUSINESS.phoneHref} className="hover:text-copper-500">{BUSINESS.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-1 text-copper-500 shrink-0" />
                <a href={BUSINESS.emailHref} className="hover:text-copper-500 break-all">{BUSINESS.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 text-copper-500 shrink-0" />
                <span>Serving the greater Spokane area</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Big logotype */}
        <div className="pt-16 overflow-hidden">
          <div className="font-display text-[18vw] leading-none tracking-tightest text-bone-50/[0.06] select-none pointer-events-none whitespace-nowrap">
            SPOKANE.GUTTER
          </div>
          <div className="flex flex-col md:flex-row justify-between gap-4 mt-8 text-xs text-bone-400 font-heading">
            <span>© {new Date().getFullYear()} {BUSINESS.name}. Licensed and insured in Washington State.</span>
            <span>Site by the shop. Built for the Inland Northwest.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
